import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getPageLinks, PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLink, getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NewListImageVideo, { NewListImageVideoStyle } from "@sunSource/Components/NewsList/NewListImageVideoComponent";
import NewsListTag, { NewsListTagStyle } from "@sunSource/Components/NewsList/NewsListTagComponent";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import TabsSearchTermHandler from "@sunSource/Store/Data/TabsData/Handler/TabsSearchTermHandler";
import { NewsPageModel } from "@sunSource/Store/Data/TabsData/TabsDataState";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { useEffect, useState } from "react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import siteMessage from "@insite/client-framework/SiteMessage";

const enum fields {
    defaultPageSize = "defaultPageSize",
    showImage = "showImage",
    showNewsSummary = "showNewsSummary",
    showAuthor = "showAuthor",
    showPublishDate = "showPublishDate",
    showReadMoreButton = "showReadMoreButton",
    showTitle = "showTitle",
    showTags = "showTags",
    showVideo = "showVideo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.showImage]: boolean;
        [fields.showNewsSummary]: boolean;
        [fields.showAuthor]: boolean;
        [fields.showPublishDate]: boolean;
        [fields.showReadMoreButton]: boolean;
        [fields.showTitle]: boolean;
        [fields.showTags]: boolean;
        [fields.showVideo]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: WidgetProps) => {

    const currentPageNodeId = state.links.pageTypesToNodeId["NewsListPage"];
    const pages = getDataView(state.data.pages, { parentNodeId: currentPageNodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: currentPageNodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: currentPageNodeId,
        },
        activeTab: state.sunSourceState.TabsActiveData.activeTab,
        activeTabData: state.sunSourceState.TabsActiveData.listActiveTabData,
        tabsSearchTerm: state.sunSourceState.TabsActiveData.tabsSearchTerm,
        isLoading: state.sunSourceState.TabsActiveData.isLoading,
        tagsName: state.sunSourceState.TabsActiveData.tagsName,
        state
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
    TabsSearchTermHandler,
    loadCategory
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: GridContainerProps;
    newsWrapper?: GridItemProps;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    buttonWrapper?: InjectableCss;
    test?: InjectableCss;
    specialAlert?: InjectableCss;
    main?: InjectableCss;
    showOnlyReadMoreWrapper?: InjectableCss;
    showOnlyReadMore?: LinkPresentationProps;
    paginationWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    timeZoneText?: TypographyProps;
    dateAndTimeZoneWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noNewsFound?: TypographyProps;
    shareIcon?: InjectableCss;
    timeZoneShareImgWrapper?: InjectableCss;
    linkImage?: LinkPresentationProps;
    sectionTitle?: TypographyProps;
    newsListImageVideo?: NewListImageVideoStyle;
    newsListTag?: NewsListTagStyle;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        variant: "h3",
        css: css`
            margin-top: 5px;
            margin-bottom: 0;
            opacity: 1;
            color: ${getColor("secondary")};
            font-weight: 600;
            letter-spacing: 2px;
            text-align: left;
            line-height: 23px;
            padding-right: 30px;
            > a {
                > span {
                    opacity: 1;
                    color: ${getColor("secondary")} !important;
                    font-weight: 600;
                    letter-spacing: 2px;
                    text-align: left;
                    line-height: 23px;
                    &:hover {
                        color: ${getColor("primary")} !important;
                    }
                }
            }
            &:hover {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    newsWrapper: {
        width: [12, 12, 6, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 15px;
            box-shadow:
                0 2px 4px rgba(0, 0, 0, 0.06),
                0 8px 16px rgba(0, 0, 0, 0.2);
            border-radius: 5px;
            padding: 0px;
            position: relative;
            &:after {
                content: "";
                position: absolute;
                bottom: 0px;
                width: 100%;
                height: 5px;
                background: #00539b;
                border-bottom-left-radius: 5px;
                border-bottom-right-radius: 5px;
            }
            
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
                nav {
                    ul {
                        li {
                            button[data-test-selector^="currentPageNumber_"]:hover {
                                background: #ffc808;
                            }
                        }
                    }
                }
            `,
        },
    },

    paginationWrapper: {
        css: css``,
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            width: 100%;
            margin-bottom: 8px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.5;
            > p {
                font-size: 15px;
                font-weight: 500;
            }
        `,
    },
    authorText: {
        css: css`
            opacity: 1;
            color: ${getColor("secondary")};
            font-size: 16px;
            font-weight: 600;
            letter-spacing: 2px;
            text-align: left;
            line-height: 18px;
            max-width: 100%;
            margin-top: 10px;
            margin-top: 0px;
            text-align: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        font-size: 18px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    publishDateText: {
        css: css`
            margin-top: 8px;
            opacity: 1;
            color: #756e6e;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 1px;
            text-align: left;
            line-height: 16px;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 5px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    timeZoneText: {
        css: css`
            margin-top: 8px;
            opacity: 1;
            color: #756e6e;
            font-size: 12px;
            font-weight: 100;
            letter-spacing: 1px;
            text-align: left;
            line-height: 16px;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 5px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            margin: 0px;
            gap: 0px;
            margin-bottom: 50px;
            > ${GridOffset} {
                margin: 0px;
                gap: 30px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            gap: 4px;
            margin-top: 20px;
        `,
    },
    readMoreLink: {
        css: css`
            width: auto;
            border-radius: 25px 0 0 25px;
            background-color: ${getColor("warning")};
            padding: 4px 10px;
            &:hover {
                background: ${getColor("warning.contrast")};
            }
            span {
                color: ${getColor("info.contrast")} !important;
                font-size: 13px !important;
                opacity: 1;
                font-weight: 500;
                letter-spacing: 0.5px;
                text-align: left;
            }
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
        `,
    },
    dateAndTimeZoneWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 5px;
        `,
    },
    specialAlert: {
        css: css`
            position: absolute;
            bottom: 20px;
            max-height: 35px;
            padding: 11px;
            border-radius: 0px 20.5px 20.5px 0px;
            opacity: 1;
            background-color: ${getColor("primary")};
            color: #fff;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 1px;
            text-align: center;
            line-height: 16px;
        `,
    },
    main: {
        css: css`
            padding-top: 20px;
            padding-bottom: 50px;
        `,
    },
    showOnlyReadMoreWrapper: {
        css: css`
            width: 100%;
        `,
    },
    showOnlyReadMore: {
        css: css`
            padding: 0px;
            span {
                font-size: 16px !important;
                opacity: 1;
                font-weight: 600;
                letter-spacing: 0.5px;
                text-align: left;
                text-transform: uppercase;
            }
        `,
    },
    contentWrapper: {
        css: css`
            position: relative;
            padding: 5px 15px 25px 15px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;
            .share-link{
                position: absolute;
                right: 20px;
                top: 10px;
            }
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin-right: 8px;
        `,
    },
    noNewsFound: {
        css: css`
            font-size: 20px;
            color: gray;
            text-align: center;
            width: 100%;
            justify-content: center;
            padding: 50px;
        `,
    },
    shareIcon: {
        css: css`
            width: 22px;
            height: 22px;
        `,
    },
    timeZoneShareImgWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    linkImage: {
        css: css`
            width: 100%;
            span {
                width: 100%;
            }
        `,
    },
    sectionTitle: {
        css: css`
                text-align: center;
                width: 100%;
                justify-content: center;
                color: #2D3435;
                line-height: 34px;
                font-size: 1.5rem;
                font-weight: 700;
                font-family: OpenSans;
                display: block;
            `,
    },
    newsListImageVideo: {
        playImageOverlay: {
            css: css`
                > img {
                    width: 100%;
                    height: auto;
                }
            `
        }
    },
    newsListTag: {
        tagItem: {
            css: css`
                 &:hover { 
                    background-color: rgb(241, 241, 241) !important;
                    color: rgb(117, 110, 110) !important;
                 }
            `
        }
    }
};

const styles = newsListViewStyles;

type LinkType = {
    title: string;
    url: string;
};

const NewsListViewForSuppliers: React.FC<Props> = (props) => {
    const [allTags, setAllTags] = useState<Record<string, TagType>>({});

    const {
        links,
        pages: { value: pages },
        fields: {
            defaultPageSize,
            showImage,
            showNewsSummary,
            showAuthor,
            showPublishDate,
            showReadMoreButton,
            showTitle,
            showTags,
            showVideo,
        },
        parameter,
        activeTab,
        activeTabData,
        tabsSearchTerm,
        isLoading,
    } = props;

    // const page = parameter.page ?? 1;
    // const pageSize = parameter.pageSize ?? defaultPageSize;

    if (typeof window === "undefined" || !window.location?.origin) return null;

    const [currentTabData, setCurrentTabData] = useState<(PageProps | NewsPageModel)[]>([]);

    useEffect(() => {

        const sortedAndPagedPages = sortBy(pages, (o) => o.fields["publishDate"]).reverse();

        const sourceData =
            activeTabData?.length || tabsSearchTerm ? activeTabData : sortedAndPagedPages;



        // const start = (page - 1) * pageSize;
        // const end = start + pageSize;

        const filteredData = sourceData;

        function isBrandMatch(brands: any, keyword: any) {
            return Object.values(brands.byId)
                .some((item: any) => item.name === keyword);
        }

        const updatedTagsData = filteredData?.filter(item => {
            const links = item?.fields?.linkssuppliers || [];

            return links.some((link: any) => {
                const category = link?.fields?.categorysupplier;
                if (!category) return false;

                const linkData = getLink(props.state, category);
                return String(props.state.components.breadcrumbs.links?.slice(-1)[0]?.children)?.toLowerCase() === linkData?.title?.toLowerCase();

            });
        }) || [];

        setCurrentTabData(updatedTagsData)

    }, [props.state])

    useEffect(() => {
        if (!props.pages.value && !props.pages.isLoading) {

            if (props.parameter.parentNodeId) {
                props.loadPagesForParent(props.parameter);
            }

        }

    }, [props.pages, props.parameter]);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (pages) {
            pages.forEach((page: any) => {
                page?.generalFields["linkssuppliers"]?.forEach((item: any) => {
                    const id = item?.fields?.categorysupplier?.value;

                    dispatch(loadCategory({ id: item?.fields?.categorysupplier?.value }))
                });
            });

        }
    }, [pages]);

    type TagType = {
        title: string;
        url: string;
    };

    const hasValidTitleAndUrl = (data: Record<string, any>): boolean => {
        if (!data || Object.keys(data).length === 0) {
            return false;
        }
        return Object.values(data).every(item => {
            return (
                item &&
                typeof item.title === "string" &&
                item.title.trim() !== "" &&
                typeof item.url === "string" &&
                item.url.trim() !== ""
            );
        });
    };

    useEffect(() => {

        const result = hasValidTitleAndUrl(allTags);
        if (!result) {
            const allLinksData = pages?.map((page, index) => {
                let categoriesTag: any = "";
                if (page.generalFields["linkssuppliers"]) {
                    const links = page.generalFields["linkssuppliers"]?.map((item: any) => {
                        return { [item?.fields?.categorysupplier.value]: getLink(props.state, item?.fields?.categorysupplier) }
                    });

                    categoriesTag = links?.length > 0 ? page.generalFields["customTags"] + page.generalFields["customTags"] ? "," : "" + links?.map((item: any) => item.title).join(",") : page.generalFields["customTags"];
                    return links;
                }
                return categoriesTag;
            })

            if (allLinksData) {
                const updatedTags = Object.fromEntries(
                    allLinksData
                        .filter(Array.isArray)
                        .flat()
                        .map(obj => {
                            const [key, value] = Object.entries(obj)[0] as [string, LinkType];

                            return [key, { title: value.title, url: value.url }];
                        })
                );
                setAllTags((prev) => {
                    if (JSON.stringify(prev) === JSON.stringify(updatedTags)) {
                        return prev;
                    }
                    return updatedTags;
                });
            }
        }

    }, [props.state.data.categories.byId, allTags])

     
    function extractPreview(html: string, limit = 300) {
        // Create temporary DOM element
        const div = document.createElement("div");
        div.innerHTML = html;

        // Extract plain text
        const text = (div.textContent || div.innerText || "")
            .replace(/\s+/g, " ") // remove extra spaces/newlines
            .trim();

        return text.slice(0, limit);
    }

    return (
        <StyledWrapper {...styles.main}>
            {isLoading ? (
                <LoadingSpinner {...styles.spinner} />
            ) : (
                <>
                    <GridContainer {...styles.listWrapper}>
                        {currentTabData && currentTabData?.length > 0 ? <GridItem width={12}><Typography as="h2" {...styles.sectionTitle}>
                            {siteMessage("RELATED BLOGS")}
                        </Typography></GridItem>
                            : null}
                        {currentTabData && currentTabData.length > 0 && (
                            currentTabData.map((page: any, index: number) => {
                                if(page?.fields?.hideFromUi === true || page?.fields?.hideFromUi === "true"){
                                    return null;
                                }
                                let customTags: string = "";

                                if (page.generalFields["linkssuppliers"]) {
                                    const linksData = page.generalFields["linkssuppliers"].map(
                                        (item: any) => allTags[item?.fields?.categorysupplier.value]
                                    );

                                    customTags =
                                        linksData?.length > 0
                                            ? page.generalFields["customTags"]
                                                ? page.generalFields["customTags"] +
                                                "," +
                                                linksData.map((i: any) => i?.title).join(",")
                                                : " " +
                                                linksData.map((i: any) => i?.title).join(",")
                                            : page.generalFields["customTags"];
                                } else {
                                    customTags = page.generalFields["customTags"];
                                }

                                return (
                                    <GridItem {...styles.newsWrapper} key={page.id}>
                                        <Link {...styles.linkImage} href={links[page.nodeId]?.url}>
                                            <NewListImageVideo
                                                videoUrl={page.fields["videoUrl"]}
                                                imageUrl={page.fields["img"]}
                                                altText={page.fields["title"]}
                                                showImage={showImage && page.fields["img"]}
                                                showVideo={
                                                    showVideo &&
                                                    !page.fields["img"] &&
                                                    page.fields["videoUrl"]
                                                }
                                                videoPlayIconFlag={
                                                    page.generalFields["videoPlayIconFlag"] === "yes"
                                                }
                                                videoPlayIcon={page.generalFields["videoPlayIcon"]}
                                                videoHeight={"200"}
                                                isPlayVideoOnclickThumbnail={false}
                                                extendedStyles={styles.newsListImageVideo}
                                                newsListpageDefaultViewAttachmentToShowFlag={true}
                                            />
                                        </Link>

                                        <StyledWrapper {...styles.contentWrapper}>
                                            {showPublishDate && page.fields["publishDate"] && (
                                                <StyledWrapper
                                                    {...styles.timeZoneShareImgWrapper}
                                                >
                                                    <StyledWrapper
                                                        {...styles.dateAndTimeZoneWrapper}
                                                    >
                                                        <Typography
                                                            {...styles.publishDateText}
                                                        >
                                                            <LocalizedDateTime
                                                                dateTime={
                                                                    new Date(
                                                                        page.fields["publishDate"]
                                                                    )
                                                                }
                                                                options={{
                                                                    year: "numeric",
                                                                    month: "short",
                                                                    day: "numeric",
                                                                }}
                                                            />
                                                        </Typography>

                                                        <Typography {...styles.timeZoneText}>
                                                            {new Date(
                                                                page.fields["publishDate"]
                                                            ).toLocaleString("en-US", {
                                                                hour: "numeric",
                                                                minute: "2-digit",
                                                                timeZoneName: "short",
                                                            })}
                                                        </Typography>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            )}

                                            {showTitle && (
                                                <Typography variant="h3" {...styles.titleText}>
                                                    <Link href={links[page.nodeId]?.url}>
                                                        {page.fields["title"]}
                                                    </Link>
                                                </Typography>
                                            )}

                                            <Link
                                                href={`mailto:?subject=${page.fields["title"]
                                                    }&body=${window.location.origin +
                                                    links[page.nodeId]?.url
                                                    }`}
                                                target="_blank"
                                                rel="nofollow"
                                                className="share-link"
                                            >
                                                <LazyImage
                                                    src="/UserFiles/Images/Icon/share.png"
                                                    {...styles.shareIcon}
                                                />
                                            </Link>

                                            {showNewsSummary && (
                                                <StyledWrapper {...styles.summaryWrapper}>
                                                    {extractPreview(page.fields["newsSummary"] || page.fields["newsContent"])}
                                                </StyledWrapper>
                                            )}

                                            {showAuthor && page.fields["author"] && (
                                                <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                                    <Typography {...styles.authorText}>
                                                        {page.fields["author"]}
                                                    </Typography>
                                                </StyledWrapper>
                                            )}

                                            <StyledWrapper {...styles.buttonWrapper}>
                                                {showReadMoreButton && (
                                                    <Link href={links[page.nodeId]?.url} {...styles.showOnlyReadMore}
                                                        data-test-selector={`newsList_ReadMore_${page.fields["title"]}`} >
                                                        {translate(
                                                            `${page.fields["videoUrl"]
                                                                ? "Watch"
                                                                : "Read"
                                                            }`
                                                        )}
                                                    </Link>
                                                )}
                                            </StyledWrapper>

                                            {showTags && customTags.length > 0 && (
                                                <NewsListTag
                                                    isonClickActiontoPerform={false}
                                                    tags={customTags}
                                                    extendedStyles={styles.newsListTag}
                                                />
                                            )}
                                        </StyledWrapper>
                                    </GridItem>
                                );
                            })
                        )
                        }
                    </GridContainer>

                </>
            )}
        </StyledWrapper>
    );
};


const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(NewsListViewForSuppliers)),
    definition: {
        group: "News List",
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
                sortOrder: 1,
            },
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showVideo,
                displayName: "Show Video",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 4,
            },
            {
                name: fields.showNewsSummary,
                displayName: "Show Summary",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 5,
            },
            {
                name: fields.showAuthor,
                displayName: "Show Author",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showPublishDate,
                displayName: "Show PublishDate",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 7,
            },
            {
                name: fields.showTags,
                displayName: "Show Tags",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showReadMoreButton,
                displayName: "Show ReadMore Button",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 9,
            },
        ],
    },
};

export default widgetModule;
